import i18n, { initI18n, loadResources } from "@tecma/i18n";
import { all, call, put, select, take } from "redux-saga/effects";
import { applicationStateActions } from "store/slices/applicationState/slice";
import translation from "locales/en-GB/translation.json";
import { loadScssFromDB } from "utils/loadScssFromDB";
import { logger } from "@tecma/logs-fe";
import { AvailableLanguage } from "@tecma/ds/lib/components/LanguageSelector/constants";

import {
  dateSelectorActionTypes,
  dateSelectorActions,
} from "store/sagas/dates/dates.actions";
import { filtersActions } from "store/sagas/filters/filters.actions";
import { getInitialDates } from "utils/dates";
import ProjectService from "services/Project";
import { getRoomSelectorConfigurationByProjectId } from "services/RoomSelector";
import { ProjectInfo } from "types/project";
import { RoomSelectorConfiguration } from "types/roomSelector";
import { environmentSelectors } from "store/slices/environment/selector";
import { EnvironmentState } from "store/slices/environment/slice";
import { authActions } from "store/sagas/auth/auth.actions";
import { detectUrl } from "utils/detectUrl";

import { applicationActions } from "../application.actions";

function* getProjectInfoSaga() {
  try {
    const environment: EnvironmentState = yield select(
      environmentSelectors.environment,
    );
    const projectInfo: ProjectInfo = yield ProjectService.getProjectInfo(
      `${environment.REACT_APP_PROJECT_API_REST_URI}${environment.REACT_APP_PROJECT_API_BASE_URL}`,
      environment.REACT_APP_HOST,
      detectUrl(environment.REACT_APP_HOST),
    );
    return projectInfo;
  } catch (err) {
    logger.error(err);
    return undefined;
  }
}

function* getRoomSelectorConfigurationSaga(projectId: string) {
  try {
    const environment: EnvironmentState = yield select(
      environmentSelectors.environment,
    );
    const roomSelectorConfiguration: RoomSelectorConfiguration =
      yield getRoomSelectorConfigurationByProjectId(
        `${environment.REACT_APP_PRODUCT_CONFIGURATION_API_REST_URI}${environment.REACT_APP_ROOM_SELECTOR_API_BASE_URL}`,
        projectId,
      );
    return roomSelectorConfiguration;
  } catch (err) {
    logger.error(err);
    return undefined;
  }
}

function* initTecmaI18NSaga() {
  const environment: EnvironmentState = yield select(
    environmentSelectors.environment,
  );
  yield initI18n(
    translation,
    {
      bucketBase: `${environment?.REACT_APP_BUCKET_BASEURL}`,
      productName: `${environment?.REACT_APP_PRODUCT_NAME}`,
      apiBase: `${environment?.REACT_APP_API_URI}`,
    },
    "en-GB",
  );
}

function* loadI18NResources(projectInfo: ProjectInfo) {
  yield loadResources({
    id: projectInfo.id,
    displayName: projectInfo.displayName,
    languages: projectInfo?.languages ?? ["it-IT", "en-GB"],
    fetchGlobalTranslations: true,
  });
}

export function* applicationInitSaga() {
  try {
    const initData: [void, void, ProjectInfo] = yield all([
      put(applicationStateActions.loadingRequired()),
      call(initTecmaI18NSaga),

      call(getProjectInfoSaga),
    ]);
    const projectInfo: ProjectInfo = initData?.[2];
    if (!projectInfo?.languages?.includes(i18n.language as AvailableLanguage)) {
      const formattedLanguage = projectInfo?.languages?.find((lang) =>
        lang.startsWith(i18n.language),
      );
      i18n.changeLanguage(formattedLanguage);
    }

    if (projectInfo) {
      yield call(loadI18NResources, projectInfo);
      yield all([
        call(loadScssFromDB, projectInfo),
        put(applicationStateActions.applicationProjectInfoLoaded(projectInfo)),
      ]);
      const roomSelectorConfiguration: RoomSelectorConfiguration = yield call(
        getRoomSelectorConfigurationSaga,
        projectInfo.id,
      );
      yield put(
        applicationStateActions.roomSelectorConfigurationLoaded(
          roomSelectorConfiguration,
        ),
      );
      const queryParams = new URLSearchParams(window.location.search);
      const clientId = queryParams.get("clientId");
      const colivingConfiguration = localStorage.getItem(
        "COLIVING_configuration",
      );
      if (colivingConfiguration && clientId) {
        const colivingConfigurationParsed = JSON.parse(colivingConfiguration);
        const { spaceId, checkIn, checkOut } =
          colivingConfigurationParsed[projectInfo.id];
        yield put(
          applicationActions.saveQuoteRequested({
            spaceId,
            checkIn,
            checkOut,
          }),
        );
      }
      yield put(filtersActions.getProjectFiltersFields());
      yield take(filtersActions.filterLoadingCompleted);
      const { startDate, endDate } = getInitialDates(
        projectInfo?.rentConstraints,
      );
      yield put({
        type: dateSelectorActionTypes.datesInitRequested,
        payload: {
          startDate,
          endDate,
        },
      });
      yield take(dateSelectorActions.datesInitCompleted);
      yield put(authActions.checkUserAuthentication());
      yield all([
        put(applicationStateActions.applicationInitDone()),
        put(applicationStateActions.loadingStopRequired()),
      ]);
    }
  } catch (err) {
    logger.error(err);
  }
}
